import { Button, Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirProposalType, DocumentDirectoryType } from '@econtractor/types';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';

interface DocumentCardProps {
    document: DirProposalType;
    canEdit?: boolean;
    onEdit: (documentId: number) => void;
    onDuplicate: (documentId: number) => void;
    onDelete: (document: DirProposalType) => void;
}

const DOCUMENT_TYPE_BADGE_CLASS: Record<DocumentDirectoryType, string> = {
    contract: 'bg-light-primary text-primary',
    invoice: 'bg-light-info text-info',
    act: 'bg-light-success text-success',
    other: 'bg-light-secondary text-secondary',
};

const DocumentCard = ({ document, canEdit = false, onEdit, onDuplicate, onDelete }: DocumentCardProps) => {
    const { t } = useTranslation(['Document', 'common']);
    const typeKey = (document.proposalType || 'other') as DocumentDirectoryType;
    const typeLabel = t(`documentTypes.${typeKey}`, { defaultValue: typeKey });
    const badgeClass = DOCUMENT_TYPE_BADGE_CLASS[typeKey] || DOCUMENT_TYPE_BADGE_CLASS.other;

    return (
        <Card className="h-100">
            <Card.Body>
                <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <h6 className="mb-0 cursor-pointer text-break" onClick={() => onEdit(document.ID)}>
                        {document.proposalName || t('card.noName')}
                    </h6>
                    <span className={`badge flex-shrink-0 ${badgeClass}`}>{typeLabel}</span>
                </div>
                <div className="text-muted fs-12 mb-0">{document.proposalComment || t('card.noComment')}</div>
            </Card.Body>
            {canEdit ? (
                <Card.Footer className="bg-transparent border-0 pt-0">
                    <div className="d-flex justify-content-end align-items-center gap-2">
                        <EToolTip title={t('card.duplicate')}>
                            <Button
                                size="sm"
                                variant="light"
                                className="px-2"
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    onDuplicate(document.ID);
                                }}
                                aria-label={t('card.duplicate')}
                            >
                                <SolarIcon name="copy" />
                            </Button>
                        </EToolTip>
                        <Button size="sm" variant="light" onClick={() => onEdit(document.ID)}>
                            {t('common:buttons.change')}
                        </Button>
                        <Button size="sm" variant="light-danger" onClick={() => onDelete(document)}>
                            {t('common:buttons.delete')}
                        </Button>
                    </div>
                </Card.Footer>
            ) : null}
        </Card>
    );
};

export default DocumentCard;
